/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { type OnInit, Component, Injector, Input } from '@angular/core';
import { type AbstractControl, type ValidationErrors, Validators } from '@angular/forms';
import { type Command, type SelectOption, DMValidators, FormControl, FormGroup, FormSlideWindowAbstract } from '@dm/core';
import { ToolService } from 'app/common/services';
import { type CIFSShare, type ExtensionFiltering, type NFSShare } from 'app/common/types/mo';
import { SWITCH } from 'app/config/switch';

import { ExtensionFilteringStrService, RuleTypeE, ShareMoTypeE } from '../extension-filtering-str.service';
import { ExtensionFilteringService } from '../extenxion-filtering.service';

@Component({
    selector: 'app-add-extension-filtering',
    templateUrl: './add-extension-filtering.component.html',
    styleUrls: ['./add-extension-filtering.component.scss'],
})
export class AddExtensionFilteringComponent extends FormSlideWindowAbstract implements OnInit {
    @Input() share: CIFSShare | NFSShare;
    @Input() shareType: ShareMoTypeE;
    @Input() data: ExtensionFiltering;

    i18n = this.str.i18n;
    SWITCH = SWITCH;

    ruleTypeOpt = this.str.ruleTypeOpt;
    vm: FormGroup;
    errorMessage: any;
    loading = false;
    rulesMaxLength = 127;
    // 侧滑框标题
    title = this.i18n.createExtensionFilteringTitle;
    helpKey = '';
    operations: SelectOption[] = [];

    constructor(
        injector: Injector,
        public str: ExtensionFilteringStrService,
        private service: ExtensionFilteringService,
        private toolService: ToolService,
    ) {
        super(injector);
    }

    ngOnInit(): void {
        this.operations = this.shareType === ShareMoTypeE.NFS ?
            this.str.nfsFileOperationsOpt :
            this.str.cifsFileOperationsOpt;

        super.ngOnInit();

        // 修改入口初始化参数
        if (this.data) {
            this.initModifyValue();
            this.title = this.i18n.modifyExtensionFilteringTitle;
            this.helpKey = this.shareType === ShareMoTypeE.NFS ? 'nfsfilefilterModify' : 'cifsfilefilterModify';
        } else {
            this.helpKey = this.shareType === ShareMoTypeE.NFS ? 'nfsfilefilterAdd' : 'cifsfilefilterAdd';
        }

        this.windowVisible = true;
        this.pageReady = true;
    }

    initVm(): void {
        this.vm = new FormGroup({
            rules: new FormControl([], {
                validators: [
                    Validators.required,
                    this.rulesLengthValidator,
                    DMValidators.multiInputValidator(true, [
                        this.rulesValidator,
                    ]),
                ],
            }),
            ruleType: new FormControl(RuleTypeE.Denied, {
                validators: [
                    Validators.required,
                ],
            }),
            operations: new FormControl(this.operations.map((item) => item.value), {
                validators: [this.operationsValidator],
            }),
        }, { updateOn: 'blur' });

        this.errorMessage = {
            rules: {
                required: this.toolService.requiredErr(this.i18n.fileNameExtension),
                rulesMaxLengthErr: this.i18n.rulesMaxLengthErr(this.rulesMaxLength),
                extensionErr: this.i18n.extensionErr,
            },
            operations: {
                required: this.i18n.operationsErr,
            },
        };
    }

    initModifyValue(): void {
        this.vm.get('rules').setValue(this.data.FILEEXTENSIONNAME.split(','));
        this.vm.get('ruleType').setValue(this.data.RULETYPE);
        this.vm.get('operations').setValue(this.data.FILEOPERATIONS.split(',').map((value) => Number(value)));

        this.vm.restore();
    }

    buildCreateCmds(): Command[] {
        const cmds: Command[] = [];

        const params: ExtensionFiltering = {
            FILEEXTENSIONNAME: this.vm.value.rules.join(','),
            PARENTID: this.share.ID,
            PARENTTYPE: this.shareType,
            RULETYPE: this.vm.value.ruleType,
            FILEOPERATIONS: this.vm.value.operations.join(','),
            vstoreId: this.share.vstoreId,
        };

        cmds.push({
            name: this.i18n.createExtensionFilteringAction(params.FILEEXTENSIONNAME),
            action: this.service.createExtensionFiltering(params),
        });

        return cmds;
    }

    buildModifyCmds(): Command[] {
        const cmds: Command[] = [];
        const changedValue = this.vm.changedValue;

        const params: ExtensionFiltering = {
            PARENTID: this.share.ID,
            PARENTTYPE: this.shareType,
            vstoreId: this.share.vstoreId,
        };

        if (Object.prototype.hasOwnProperty.call(changedValue, 'rules')) {
            params.FILEEXTENSIONNAME = changedValue.rules.join(',');
        }

        if (Object.prototype.hasOwnProperty.call(changedValue, 'ruleType')) {
            params.RULETYPE = changedValue.ruleType;
        }

        if (this.operationsChanged()) {
            params.FILEOPERATIONS = changedValue.operations.join(',');
        }

        cmds.push({
            name: this.i18n.modifyExtensionFilteringAction(this.data.FILEEXTENSIONNAME),
            action: this.service.modifyExtensionFiltering(this.data.ID, params),
        });

        return cmds;
    }

    buildCmds(): Command[] {
        let cmds: Command[] = [];

        if (this.data) {
            cmds = this.buildModifyCmds();
        } else {
            cmds = this.buildCreateCmds();
        }

        return cmds;
    }

    isModified(): boolean {
        if (this.data) {
            const changedValue = this.vm.changedValue;
            if (changedValue) {
                // 操作类型单独判断
                delete changedValue.operations;
                return Object.keys(changedValue).length > 0 || this.operationsChanged();
            } else {
                return false;
            }
        } else {
            return this.vm.valueChanged;
        }
    }

    /**
     * 判断操作类型是否有变化
     */
    operationsChanged(): boolean {
        // 创建页面因为有默认值，默认为已修改状态
        if (!this.data) {
            return true;
        }
        // 操作类型原始值和当前值排序后转换成字符串比较是否有改动
        const operationsOriginValue = this.data.FILEOPERATIONS
            .split(',').map((value) => Number(value))
            .sort((a, b) => a - b).toString();
        const operationsCurrentValue = this.vm.get('operations').value
            .sort((a: number, b: number) => a - b).toString();
        return operationsOriginValue !== operationsCurrentValue;
    }

    rulesLengthValidator = (control: AbstractControl): ValidationErrors | null => {
        const isValid = control.value.join(',').length <= this.rulesMaxLength;

        return isValid ? null : { rulesMaxLengthErr: true };
    };

    rulesValidator = (control: AbstractControl): ValidationErrors | null => {
        const reg = /^[0-9a-zA-z`~!@#$%^&*()\-=_+\\|\/\[{\]}'";:?.>,<\x20]{1,127}$/;
        const dataArr = control.value.split('');
        let starNum = 0;

        if (control.value === '*') {
            return { extensionErr: true };
        }

        dataArr.forEach((item: string) => {
            if (item === '*') {
                starNum++;
            }
        });

        let isValid = reg.test(control.value);

        if (starNum > 1 || (starNum !== 0 && dataArr[dataArr.length - 1] !== '*')) {
            isValid = false;
        }

        return isValid ? null : { extensionErr: true };
    };

    /**
     * 操作类型校验
     */
    operationsValidator = (control: AbstractControl): ValidationErrors | null => {
        if (control.value.length === 0) {
            return { required: true };
        }

        return null;
    };
}
